import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Modal,
    TextField,
    Tooltip,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { styled } from "@mui/material/styles";
import axios from "axios";
import Header from "../../components/Header";

const DropZone = styled(Box)(({ theme }) => ({
    border: "2px dashed #00FFFF",
    borderRadius: "8px",
    padding: "20px",
    textAlign: "center",
    color: "#00FFFF",
    cursor: "pointer",
    marginBottom: "16px",
}));

// ==========================================
// ENDPOINTS
// ==========================================
const API_FINANCE_EXPENSES = "https://figazuresql.swancreate.com/api/swa/FINANCE_Expenses"; // New endpoint for FINANCE_Expenses
const API_SCAN_INVOICE = "https://invoice.swancreate.com/scan_invoice/";
const API_UPLOAD_INVOICES = "https://invoice.swancreate.com/api/upload/invoices";

// ==========================================
// MAIN COMPONENT
// ==========================================
const DocumentManagementPage = () => {
    const [invoiceData, setInvoiceData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Filters
    const [monthFilter, setMonthFilter] = useState("");
    const [vendorFilter, setVendorFilter] = useState("");
    const [invoiceNumberFilter, setInvoiceNumberFilter] = useState("");

    // Modal for viewing image
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState("");

    // ----------------------------------------
    // Fetch invoices from FINANCE_Expenses API on mount
    // ----------------------------------------
    useEffect(() => {
        const fetchInvoices = async () => {
            setLoading(true);
            try {
                console.log("Fetching invoices from FINANCE_Expenses API...");
                const response = await axios.get(API_FINANCE_EXPENSES);
                console.log("Raw invoices response:", response.data);

                let data = [];
                if (Array.isArray(response.data)) {
                    data = response.data;
                } else if (response.data.invoice_data) {
                    data = response.data.invoice_data;
                } else {
                    console.error("Unexpected invoices response structure:", response.data);
                }
                console.log("Fetched invoices:", data);
                // Do not filter out invoices without ImageName (we want to display all)
                setInvoiceData(data);
                setFilteredData(data);
            } catch (err) {
                console.error("Error fetching invoices:", err);
                setError("Error fetching invoices.");
            } finally {
                setLoading(false);
            }
        };
        fetchInvoices();
    }, []);

    // Compute vendor options
    const vendorOptions = Array.from(
        new Set(
            invoiceData
                .map((invoice) => invoice.VendorName?.value || invoice.VendorName || "")
                .filter((name) => name.trim() !== "")
        )
    ).sort((a, b) => a.localeCompare(b));

    // Filter invoices by Month, Vendor, and Invoice Number
    useEffect(() => {
        let data = invoiceData;
        if (monthFilter) {
            data = data.filter((invoice) => {
                if (invoice.InvoiceDate) {
                    const date = new Date(invoice.InvoiceDate.value || invoice.InvoiceDate);
                    return date.getMonth() + 1 === parseInt(monthFilter, 10);
                }
                return false;
            });
        }
        if (vendorFilter) {
            data = data.filter((invoice) => {
                const vendor = invoice.VendorName?.value || invoice.VendorName || "";
                return vendor.toLowerCase().includes(vendorFilter.toLowerCase());
            });
        }
        if (invoiceNumberFilter) {
            data = data.filter((invoice) => {
                const invNum =
                    invoice.InvoiceNumber?.value ||
                    invoice.InvoiceNumber ||
                    invoice.InvoiceId ||
                    "";
                return invNum.toLowerCase().includes(invoiceNumberFilter.toLowerCase());
            });
        }
        setFilteredData(data);
    }, [monthFilter, vendorFilter, invoiceNumberFilter, invoiceData]);

    // Table columns
    const columns = [
        {
            field: "apiid",
            headerName: "API ID",
            flex: 1,
            valueGetter: (params) => params.row.apiid || "N/A",
        },
        {
            field: "VendorName",
            headerName: "Vendor Name",
            flex: 1,
            valueGetter: (params) =>
                params.row.VendorName?.value || params.row.VendorName || "N/A",
        },
        {
            field: "InvoiceNumber",
            headerName: "Invoice Number",
            flex: 1,
            valueGetter: (params) =>
                params.row.InvoiceNumber?.value ||
                params.row.InvoiceNumber ||
                params.row.InvoiceId ||
                "N/A",
        },
        {
            field: "InvoiceDate",
            headerName: "Invoice Date",
            flex: 1,
            valueGetter: (params) => {
                const val = params.row.InvoiceDate?.value || params.row.InvoiceDate;
                if (!val) return "N/A";
                const dt = new Date(val);
                return dt.toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                });
            },
        },
        {
            field: "InvoiceTotal",
            headerName: "Invoice Total",
            flex: 1,
            valueGetter: (params) => {
                let val = params.row.InvoiceTotal?.value || params.row.InvoiceTotal || 0;
                return new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(val);
            },
        },
        {
            field: "AmountDue",
            headerName: "Amount Due",
            flex: 1,
            valueGetter: (params) => {
                let val = params.row.AmountDue?.value || params.row.AmountDue || 0;
                return new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(val);
            },
        },
        {
            field: "viewImage",
            headerName: "View Invoice",
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                // Use the ImageName field to build the full URL.
                const imageName = params.row.ImageName;
                const fullImageUrl = imageName
                    ? `https://invoice.swancreate.com/invoice/${imageName}`
                    : "";
                return (
                    <Tooltip title="Click to view and zoom invoice image">
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                if (fullImageUrl) {
                                    setSelectedImage(fullImageUrl);
                                    setImageModalOpen(true);
                                } else {
                                    alert("No image available for this invoice.");
                                }
                            }}
                        >
                            View Invoice
                        </Button>
                    </Tooltip>
                );
            },
        },
    ];

    return (
        <Box
            m="20px"
            sx={{
                backgroundColor: "#1A1A2E",
                color: "#EFFFFD",
                minHeight: "100vh",
                padding: "20px",
                borderRadius: "10px",
                boxShadow: "0px 0px 30px 5px rgba(0,255,255,0.5)",
            }}
        >
            <Header
                title="Document Management"
                subtitle="Search and filter invoices with images. Click 'View Invoice' to see the image."
                sx={{ color: "#FFFFFF" }}
                titleColor="#FFFFFF"
            />

            {/* Filter Section */}
            <Box display="flex" gap={2} mb={2} alignItems="center">
                <TextField
                    label="Month (1-12)"
                    variant="outlined"
                    size="small"
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(e.target.value)}
                    InputProps={{
                        sx: { backgroundColor: "#FFFFFF", borderRadius: "4px", color: "#000000" },
                    }}
                    InputLabelProps={{ sx: { color: "#000000" } }}
                />
                <FormControl
                    variant="outlined"
                    size="small"
                    sx={{ backgroundColor: "#FFFFFF", borderRadius: "4px", minWidth: 150 }}
                >
                    <InputLabel sx={{ color: "#000000" }}>Vendor</InputLabel>
                    <Select
                        label="Vendor"
                        value={vendorFilter}
                        onChange={(e) => setVendorFilter(e.target.value)}
                        sx={{ color: "#000000" }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {vendorOptions.map((vendor, idx) => (
                            <MenuItem key={idx} value={vendor}>
                                {vendor}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Invoice Number"
                    variant="outlined"
                    size="small"
                    value={invoiceNumberFilter}
                    onChange={(e) => setInvoiceNumberFilter(e.target.value)}
                    InputProps={{
                        sx: { backgroundColor: "#FFFFFF", borderRadius: "4px", color: "#000000" },
                    }}
                    InputLabelProps={{ sx: { color: "#000000" } }}
                />
                <Button
                    variant="contained"
                    sx={{
                        backgroundColor: "#00CED1",
                        color: "#121212",
                        fontWeight: "bold",
                        "&:hover": { backgroundColor: "#20B2AA" },
                    }}
                    onClick={() => {
                        setMonthFilter("");
                        setVendorFilter("");
                        setInvoiceNumberFilter("");
                    }}
                >
                    Clear Filters
                </Button>
            </Box>

            {loading && <CircularProgress />}
            {error && (
                <Typography color="error" mb={2}>
                    {error}
                </Typography>
            )}

            {/* Invoice Data Table */}
            <Box
                sx={{
                    height: "600px",
                    backgroundColor: "#0F3460",
                    color: "#EFFFFD",
                    "& .MuiDataGrid-root": { border: "none" },
                    "& .MuiDataGrid-row:nth-of-type(even)": {
                        backgroundColor: "rgba(0,191,255,0.1)",
                    },
                    "& .MuiDataGrid-row:nth-of-type(odd)": {
                        backgroundColor: "rgba(0,191,255,0.2)",
                    },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: "#16213E",
                        color: "#EFFFFD",
                    },
                    "& .MuiCheckbox-root": {
                        color: "#00FFFF",
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                        color: "#EFFFFD",
                    },
                }}
            >
                <DataGrid
                    rows={filteredData}
                    columns={columns}
                    components={{ Toolbar: GridToolbar }}
                    getRowId={(row) =>
                        row.apiid || row.InvoiceId?.value || row.ID || row.InvoiceNumber || Math.random()
                    }
                    disableSelectionOnClick
                    pageSize={25}
                    rowsPerPageOptions={[25]}
                    density="comfortable"
                    autoHeight
                />
            </Box>

            {/* IMAGE MODAL */}
            <Modal
                open={imageModalOpen}
                onClose={() => setImageModalOpen(false)}
                aria-labelledby="image-modal-title"
                aria-describedby="image-modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        bgcolor: "#1A1A2E",
                        boxShadow: "0px 0px 30px 5px rgba(0,255,255,0.5)",
                        p: 2,
                        borderRadius: "8px",
                        maxWidth: "90%",
                        maxHeight: "90%",
                        overflow: "auto",
                    }}
                >
                    <img
                        src={selectedImage}
                        alt="Scanned Invoice"
                        style={{ width: "100%", height: "auto" }}
                    />
                </Box>
            </Modal>
        </Box>
    );
};

export default DocumentManagementPage;
